<template>
  <div class="homepage">
    <DomainConnectModal v-model="displayConnectDomainModal" />
    <Onboarding v-if="!$store.getters['getCurrentUser'].onboarded" />
    <div v-else>
      <h1 class="homepage-title">{{ $t("custom.homepage.hello") }}, {{ $store.state.user.firstName }}!</h1>
      <template
        v-if="!$store.state.user.verified && $store.state.displayVerifyEmailDisclaimer && $can('view', 'email verify')">
        <VaDisclaimer
          class="verify-email__disclaimer spacing-bottom--24"
          :type="'warning'"
          :title="$t('settings.verify_email_title')"
          :text="$t('settings.verify_email_subtitle', { email: $store.state.user.email })">
          <template #actions>
            <v-btn
              class="spacing-right--16"
              :loading="emailResendLoading"
              @click="resendEmail"
              outlined
              color="same-menu"
              height="38">
              {{ $t("settings.resend_email") }}
            </v-btn>
            <v-icon @click="hideEmailVerificationDisclaimer" class="verify-email__disclaimer-close" color="same-menu"
              >mdi-close</v-icon
            >
          </template>
        </VaDisclaimer>
      </template>

      <div class="card">
        <div class="card-wrapper">
          <div>
            <h3 class="card-title">{{ $t("custom.homepage.card_title") }}</h3>
            <p class="card-subtitle">{{ $t("custom.homepage.card_subtitle") }}</p>
          </div>
          <v-btn plain to="domains" :color="'icon'">{{ $t("custom.homepage.view_all") }}</v-btn>
        </div>
        <div class="domains_list">
          <div v-for="domain in displayedDomains" :key="domain.id" class="domains_list-item">
            <router-link :to="{ name: 'domains_show', params: { id: domain.id }, query: { tab: 'pages' } }">
              <div class="domains_list-item__icon">
                <v-img :src="$vuetify.theme.dark ? webDarkImg : webLightImg"></v-img>
              </div>

              <div class="domains_list-item__text">
                <div class="domains_list-item__title">
                  <v-tooltip bottom v-if="domain.status === DOMAIN_STATUS.CONNECTING">
                    <template #activator="{ on, attrs }">
                      <span v-on="on" v-bind="attrs" class="domains_list-item__dot gray"> </span>
                    </template>
                    <p class="tooltip-content">{{ $t("resources.domains.deployment_status.configurating") }}</p>
                  </v-tooltip>
                  <v-tooltip bottom v-if="domain.status === DOMAIN_STATUS.CONNECTED">
                    <template #activator="{ on, attrs }">
                      <span v-on="on" v-bind="attrs" class="domains_list-item__dot green"> </span>
                    </template>
                    <p class="tooltip-content">{{ $t("resources.domains.deployment_status.connected") }}</p>
                  </v-tooltip>
                  <v-tooltip bottom v-if="domain.status === DOMAIN_STATUS.ERROR">
                    <template #activator="{ on, attrs }">
                      <span v-on="on" v-bind="attrs" class="domains_list-item__dot red"> </span>
                    </template>
                    <p class="tooltip-content">{{ $t("resources.domains.deployment_status.error") }}</p>
                  </v-tooltip>
                  {{ domain.name }}
                </div>
                <div class="domains_list-item__subtitle">{{ domain.url }}</div>
              </div>

              <div class="domains_list-item__arrow">
                <v-icon>{{ icons.mdiChevronRight }}</v-icon>
              </div>
            </router-link>
          </div>

          <div v-if="domainsList.length < 3" class="domains_list__new">
            <a @click="connectDomainHandler">
              <v-icon color="primary">{{ icons.mdiPlus }}</v-icon>
              <span>{{ $t("custom.homepage.connect_domain") }}</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { mdiChevronRight, mdiPlus } from "@mdi/js";
  import webLightImg from "@/assets/images/svg/web.svg";
  import webDarkImg from "@/assets/images/svg/web-dark.svg";
  import VaDisclaimer from "@/components/custom/VaDisclaimer";
  import Onboarding from "./Onboarding.vue";
  import { DOMAIN_STATUS } from "@/constant";
  import DomainConnectModal from "@/components/custom/DomainConnectModal";
  import definedAbility from "@/ability";
  import { formErrorMessage } from "@/utility/formErrorMessage";

  export default {
    components: {
      Onboarding,
      VaDisclaimer,
      DomainConnectModal,
    },
    data() {
      return {
        ability: definedAbility(),
        webLightImg,
        webDarkImg,
        icons: {
          mdiChevronRight,
          mdiPlus,
        },
        domainsList: [],
        emailResendLoading: false,
        displayConnectDomainModal: false,
        DOMAIN_STATUS,
      };
    },
    async created() {
      let { data } = await this.getAllDomains();
      this.domainsList = data;
    },
    computed: {
      displayedDomains() {
        return this.domainsList?.slice(0, 3) || [];
      },
    },
    methods: {
      async getAllDomains() {
        try {
          return await this.$admin.dataProvider.getList("domains", {
            disablePagination: true,
          });
        } catch (e) {
          console.error(e);

          return { data: [] };
        }
      },
      async resendEmail() {
        this.emailResendLoading = true;
        try {
          const { data } = await this.$admin.dataProvider.customHttpCall(
            "POST",
            `users/${this.$store.state.user.email}/verify/resend`,
          );

          if (data.result === "SUCCESS") {
            this.$admin.toast.success(data.message);
          }
        } catch (err) {
          this.$admin.toast.error(formErrorMessage(err, this.$t("generic.error")));
        } finally {
          this.emailResendLoading = false;
        }
      },
      hideEmailVerificationDisclaimer() {
        this.$store.commit("SET_DISPLAY_VERIFY_EMAIL_DISCLAIMER", false);
      },
      async connectDomainHandler() {
        if (this.ability.can("view", "original domain connect")) {
          this.$router.push({ name: "domains_create" });

          return;
        }
        this.displayConnectDomainModal = true;
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import "../@core/preset/preset/functions";

  .verify-email__disclaimer-close {
    cursor: pointer;
  }
  .homepage {
    &-title {
      font-weight: 500;
      font-size: 34px;
      line-height: 42px;
      color: get-color("headline");
      margin-bottom: 24px;
    }
  }

  .card {
    background: get-color("white");
    padding: 16px;
    box-shadow: 0 2px 10px rgba(69, 64, 78, 0.1);
    border-radius: 6px;

    &-wrapper {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 16px;
    }

    &-title {
      font-weight: 500;
      font-size: 24px;
      line-height: 32px;
      color: get-color("headline");
    }

    &-subtitle {
      font-weight: 400;
      font-size: 16px;
      line-height: 24px;
      color: get-color("subtitle");
      margin-top: 6px;
    }
  }

  .domains_list {
    display: flex;

    &-item {
      border: 1px solid get-color("border");
      border-radius: 6px;

      width: 33%;
      transition: 0.28s cubic-bezier(0.4, 0, 0.2, 1);
      &:nth-child(n + 2) {
        margin-left: 32px;
      }

      a {
        padding: 14px;
        display: flex;
        align-items: center;
        width: 100%;
        height: 100%;
        text-decoration: none;
      }

      &__text {
        margin-left: 15px;
        flex: 1 1 auto;
      }

      &__title {
        font-weight: 400;
        font-size: 16px;
        line-height: 24px;
        color: get-color("headline");
        display: flex;
        align-items: center;
      }

      &__subtitle {
        font-weight: 400;
        font-size: 12px;
        line-height: 16px;
        color: get-color("subtitle");
        margin-top: 4px;
      }

      &__dot {
        width: 8px;
        height: 8px;
        background-color: get-color("success");
        display: inline-block;
        border-radius: 50%;
        margin-right: 6px;

        &.gray {
          background-color: get-color("subtitle");
        }
        &.green {
          background-color: get-color("success");
        }
        &.red {
          background-color: get-color("error");
        }
      }

      &:hover {
        background-color: get-color("primary-10-hover");
        border: 1px solid get-color("primary-20");
      }
    }

    &__new {
      width: calc(33% + 60px);
      flex: 1 1 auto;
      border: 1px dashed get-color("border");
      border-radius: 6px;
      margin-left: 32px;
      a {
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 14px;
        width: 100%;
        height: 100%;
        text-decoration: none;
        font-weight: 500;
        font-size: 12px;
        line-height: 24px;
        transition: 0.28s cubic-bezier(0.4, 0, 0.2, 1);

        &:hover {
          background-color: get-color("primary-10-hover");
          border: 1px dashed get-color("primary-20");
        }
      }
    }
  }
</style>
